import { DropdownAutoCompleteSelect } from "@/components/form-control";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { findObject } from "@/helper/array";
import { useDebounce } from "@/hooks/lib-ui";
import { usePlacesQuery } from "@/services/web/map.queries";
import { AddressInput, Address, ICoordinates } from "@/types/address.type";
import { Point } from "geojson";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

export type AddressByMapbox = { address: Address; geometry?: Point };
export type AddressComponentProps = {
  onSelectAddress?: (input: AddressByMapbox) => void;
  coordinates?: ICoordinates;
};

export const AddressComponent = ({ onSelectAddress, coordinates }: AddressComponentProps) => {
  const { setValue, getValues, control } = useFormContext<AddressInput>();
  const initString = getValues("address.streetName");
  const [address, setAddress] = useState<string>(initString ?? "");
  const searchString = useDebounce(address);
  const handleSearch = (value: string) => {
    setAddress(value);
    setValue("address.streetName", value);
  };
  const { data = [], isLoading } = usePlacesQuery({
    keyWord: searchString,
    coordinates,
  });

  const handleSelectPlace = async (value: string) => {
    const findAddressIdx = findObject(data, "value", value);
    if (findAddressIdx > -1) {
      const selectedAddress = data[findAddressIdx];
      const newPoint = selectedAddress.geometry;
      setAddress(selectedAddress.streetName);
      setValue("address", selectedAddress);
      if (onSelectAddress) {
        onSelectAddress({ address: selectedAddress, geometry: newPoint });
      }
    }
  };

  return (
    <>
      <FormField
        control={control}
        name="address.streetName"
        render={({ field: { value } }) => (
          <FormItem>
            <FormLabel>
              Address <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <DropdownAutoCompleteSelect
                onChangeValue={handleSelectPlace}
                value={value}
                displayValue={address}
                searchString={address}
                handleSearch={handleSearch}
                options={data}
                isLoading={isLoading}
                className="max-w-[459px]"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="address.streetNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Street number <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="address.province"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Province</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="address.city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              City <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="address.postalCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Postal code <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="address.country"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Country <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
